import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomDialog from "../modals/CustomDialog/CustomDialog";
import { Button } from "../forms/Buttons/Button";
import StreamChoice, { takenStreams } from "./StreamChoice";
import { getQuestionFolders, updateQuestionFolder } from "../../services/questionService";
import { getSubjectVariants } from "../../services/subjectVariantService";
import { getApiErrorMessage } from "../../utils/apiError";
import { isCanceledError } from "../../utils/examError";

/**
 * Admin: a subject's settings, its stream (КАЗ, РУС or both). `folder` is the subject (an item of GET
 * question_folders/), `examName` its exam's name; `onSaved(saved)` gets the saved folder. When the dialog opens
 * it reads the exam's other subjects (a stream the subject already has there is offered disabled) and whether
 * variants are built from the subject (then its stream no longer changes).
 */
export default function SubjectSettingsDialog({ isOpen, folder, examName, onClose, onSaved }) {
  const [t] = useTranslation();
  const [stream, setStream] = useState(null);
  const [siblings, setSiblings] = useState(null);
  const [hasVariants, setHasVariants] = useState(false);
  const [streamError, setStreamError] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen || !folder) return undefined;
    setStream(folder.stream ?? null);
    setStreamError("");
    setError("");
    setSiblings(null);
    setHasVariants(false);
    const controller = new AbortController();
    getQuestionFolders({ signal: controller.signal })
      .then((list) => setSiblings((Array.isArray(list) ? list : []).filter((f) => f.parent === folder.parent)))
      .catch((err) => {
        if (!isCanceledError(err)) setSiblings([]);
      });
    // Unknown (an error) counts as none: the server still refuses the change, and the dialog then says why.
    getSubjectVariants({ params: { folder: folder.uuid, page: 1, page_size: 1 }, signal: controller.signal })
      .then((page) => setHasVariants((page?.count ?? page?.results?.length ?? 0) > 0))
      .catch(() => {});
    return () => controller.abort();
  }, [isOpen, folder]);

  const taken = useMemo(() => takenStreams(siblings, folder?.subject?.uuid, folder?.uuid), [siblings, folder]);
  const changed = !!folder && stream !== (folder.stream ?? null);

  // The two refusals of a stream (400 {stream}, in the server's order): variants built from the subject, or the same
  // subject and stream added to the exam meanwhile. What the dialog read on opening said neither, so both are read
  // again: the choice then shows them, and the message says which one it was (else the server's own words).
  const explainRefusal = async (data) => {
    const [fresh, built] = await Promise.all([
      getQuestionFolders()
        .then((list) => (Array.isArray(list) ? list : []).filter((f) => f.parent === folder.parent))
        .catch(() => null),
      getSubjectVariants({ params: { folder: folder.uuid, page: 1, page_size: 1 } })
        .then((page) => (page?.count ?? page?.results?.length ?? 0) > 0)
        .catch(() => null),
    ]);
    if (fresh) setSiblings(fresh);
    if (built !== null) setHasVariants(built);
    if (built) setStreamError(t("folders.stream.in_use"));
    else if (fresh && takenStreams(fresh, folder.subject?.uuid, folder.uuid).has(stream)) setStreamError(t("folders.stream.duplicate"));
    else setStreamError([].concat(data.stream).join(" "));
  };

  const handleSave = async () => {
    if (!changed || saving || hasVariants || taken.has(stream)) return;
    setSaving(true);
    setStreamError("");
    setError("");
    try {
      onSaved(await updateQuestionFolder(folder.uuid, { stream }));
    } catch (err) {
      const data = err?.response?.data;
      if (data && !data.code && data.stream) await explainRefusal(data);
      else setError(getApiErrorMessage(t, err, "folders.toast.save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomDialog
      isOpen={isOpen}
      closeModal={() => !saving && onClose()}
      headerText={t("folders.subject_settings_dialog.title", { name: folder?.name || "" })}
      maxWidth="max-w-md"
    >
      <div className="mt-2 space-y-4 text-left">
        {examName && (
          <p className="text-sm text-slate-500">
            {examName} · {folder?.name}
          </p>
        )}
        <StreamChoice
          name="subject-stream"
          value={stream}
          onChange={(next) => {
            setStream(next);
            setStreamError("");
          }}
          taken={taken}
          error={streamError}
          errorId="subject-stream-error"
          disabled={saving || hasVariants}
        />
        {/* A refused save already says it under the choice. */}
        {hasVariants && !streamError ? (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-inset ring-amber-600/20">
            {t("folders.stream.in_use")}
          </p>
        ) : (
          <p className="text-xs text-slate-500">{t("folders.subject_settings_dialog.stream_note")}</p>
        )}
        {error && (
          <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
            {error}
          </p>
        )}
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button type="button" color="secondary-outline" text={t("buttons.cancel")} onClick={onClose} disabled={saving} className="max-sm:h-11" />
        <Button
          type="button"
          color="primary"
          text={t("buttons.save")}
          onClick={handleSave}
          loading={saving}
          disabled={!changed || saving || hasVariants || taken.has(stream)}
          className="max-sm:h-11"
        />
      </div>
    </CustomDialog>
  );
}
